import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchData,
  getModuleValue,
  leadingZero,
  parseCMSBold,
  useWindowSize,
} from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import PagesHeader from "@components/PagesHeader";
import NiceModal from "@ebay/nice-modal-react";
import { getCouponCatalogueDescribeAuction } from "../../../../../api/coupon";
import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../../../../api/consumer";
import DOMPurify from "dompurify";
import { ACTIVATION_CACHE_TIME, getActivationDescribe } from "@api/activation";
import StickyButton from "@components/StickyButton";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";

const calculateTimeLeft = (targetDate) => {
  const difference = new Date(targetDate) - new Date();
  let timeLeft = {};

  if (difference > 0) {
    timeLeft = {
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  } else {
    timeLeft = { hours: 0, minutes: 0, seconds: 0 };
  }

  return timeLeft;
};

export default function Auction({ currentQuestion }) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const XGA = width >= 1020;

  const {
    date,
    cost,
    limit,
    emailTicket = true,
    prizeIdent,
    desc,
  } = gameQuestionsQuery.content[0].answers[0];

  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(date));
  const [loading, setLoading] = useState(false);
  const allZeros = Object.values(timeLeft).every((value) => value === 0);

  const [error, setError] = useState({});

  const {
    data: APIData,
    isError,
    isLoading,
    refetch: refetchActivationDescribe,
  } = useQuery({
    queryKey: ["activation-describe", ident],
    queryFn: () =>
      getActivationDescribe({
        activation: {
          ident: ident,
          withSolutions: true,
        },
      }),
    retry: false,
    refetchOnMount: "always",
    staleTime: 1000 * 60 * 3,
    cacheTime: ACTIVATION_CACHE_TIME,
  });

  function compareDate(givenDate) {
    const currentDate = new Date();
    const targetDate = new Date(givenDate);

    if (currentDate < targetDate) {
      return "before";
    } else if (currentDate > targetDate) {
      return "after";
    }
  }

  const detailsQuery = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  console.log(prizeIdent);

  const { data } = useQuery({
    queryKey: ["coupon-catalogue-describe", prizeIdent],
    queryFn: () =>
      getCouponCatalogueDescribeAuction(
        prizeIdent,
        catalogueDescribeError,
        catalogueDescribeSuccess
      ),
    staleTime: 10000,
    cacheTime: 10000,
    refetchInterval: 10000,
    refetchOnMount: "always",
  });

  function catalogueDescribeSuccess() {
    if (Object.keys(error).length !== 0) {
      setError({});
    }
  }

  function catalogueDescribeError(resObj) {
    if (resObj?.data?.error !== "error_catalogue_not_found") {
      return;
    }

    const dateInfo = compareDate(date);

    const errorObj = {
      points: dateInfo === "before" ? cost : limit,
      dateInfo,
    };

    setError(errorObj);
  }

  function compareDate(givenDate) {
    const currentDate = new Date();
    const targetDate = new Date(givenDate);

    if (currentDate < targetDate) {
      return "before";
    } else if (currentDate > targetDate) {
      return "after";
    }
  }

  const calculateNextPriceChange = (date, cost, current) => {
    const pkt = 50;
    const min = 3;
    let d = new Date(date);
    const tick = (cost - current) / pkt;
    const minutes = tick * min + min;
    const newDate = new Date(d.getTime() + minutes * 60000);
    return (
      leadingZero(newDate.getHours()) + ":" + leadingZero(newDate.getMinutes())
    );
  };

  function redeemAward() {
    fetchData({
      endpoint: "/coupon/redeem",
      payload: {
        catalogue: {
          ident: DOMPurify.sanitize(prizeIdent),
        },
      },
      successCallback: (resObj) => {
        NiceModal.show("auction-modal", {
          ...gameQuestionsQuery.content[0].modalSuccessContent,
          img: getModuleValue(data?.content?.modules, "thumbnail", "file.url"),
          onClose: () => {
            navigate("/", { replace: true });
          },
        });
        detailsQuery.refetch();
      },
      errorCallback: (resObj) => {
        if (resObj?.data?.error === "error_limit_exceeded") {
          NiceModal.show("auction-modal", {
            ...gameQuestionsQuery.content[0].modalRedeemedCopy,
            img: getModuleValue(
              data?.content?.modules,
              "thumbnail",
              "file.url"
            ),
            onClose: () => {
              navigate("/", { replace: true });
            },
          });
        }

        if (resObj?.data?.error === "error_empty_coupon_database") {
          NiceModal.show("auction-modal", {
            ...gameQuestionsQuery.content[0].modalFailContent,
            img: getModuleValue(
              data?.content?.modules,
              "thumbnail",
              "file.url"
            ),
            onClose: () => {
              navigate("/activities", { replace: true });
            },
          });
        }

        detailsQuery.refetch();
      },
      setLoading: setLoading,
    });
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(date));
    }, 1000);

    return () => clearInterval(timer);
  }, [date]);

  return (
    <section className={`game-page auction-mechanics ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            {compareDate(date) === "before" ? (
              <p className="auction-start">
                Auktion startet:{" "}
                <span>{new Date(date).toLocaleString("pl-PL")}</span>
              </p>
            ) : (
              <div className="top-copy">
                <div className="status-pill">
                  <span
                    className={`${
                      data?.inStock && data?.available ? "active" : ""
                    }`}
                  >
                    {data?.inStock
                      ? "Die Auktion läuft!"
                      : "Gutscheine gesammelt!"}
                  </span>
                </div>
                {!data?.inStock && <p>AUKTION BEENDET!</p>}
              </div>
            )}
            <div
              className={`game-question ${
                allZeros && data?.available ? "active" : ""
              }`}
            >
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].question),
                }}
              />
            </div>

            <div
              className={`auction-inner ${
                allZeros && data?.available ? "active" : ""
              }`}
            >
              <div className="text">
                <p>
                  Verfügbarkeit:{" "}
                  {data?.inStock ? <span>Niedrig</span> : <span>KEINE</span>}
                </p>
                <p className="cost">
                  Startpreis: <span className="blue">{cost}</span>
                </p>
                <p>
                  Aktueller Preis:{" "}
                  <span className="blue">
                    {error?.points ? error?.points : data?.price} Punkte
                  </span>
                </p>
              </div>
              <p
                className={`discount ${data?.price <= limit ? "hidden" : ""} ${
                  allZeros && data?.available ? "" : "hidden"
                }`}
              >
                -50 Punkte
                <span>
                  {" "}
                  Zeit {calculateNextPriceChange(date, cost, data.price)}
                </span>
              </p>
            </div>
            <p className={`des ${allZeros ? "active" : ""}`}>
              {data?.inStock
                ? "Greifst du jetzt zu – oder überlässt du den Moment jemand anderem?"
                : "Viel Erfolg beim nächsten Mal!"}
            </p>
          </div>

          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />
            {gameQuestionsQuery.content[currentQuestion].desktopImgHid ? (
              selected ===
              gameQuestionsQuery.content[currentQuestion].correctAnswer ? (
                <img
                  src={
                    gameQuestionsQuery.content[currentQuestion][
                      width < 1020 ? "mobileImg" : "desktopImg"
                    ]
                  }
                  alt=""
                />
              ) : (
                <img
                  src={
                    gameQuestionsQuery.content[currentQuestion][
                      width < 1020 ? "mobileImgHid" : "desktopImgHid"
                    ]
                  }
                  alt=""
                />
              )
            ) : (
              <div className="img-wrapper">
                <img
                  src={
                    gameQuestionsQuery.content[currentQuestion][
                      width < 1020 ? "mobileImg" : "desktopImg"
                    ]
                  }
                  alt=""
                />
              </div>
            )}
            {XGA && (
              <StickyContent>
                {data?.inStock ? (
                  <div className="buttons-flex">
                    <Button
                      disabled={!allZeros || loading || !data?.available}
                      action={() => {
                        redeemAward();
                      }}
                    >
                      Meinen Moment nutzen!
                    </Button>
                  </div>
                ) : (
                  <div className="buttons-flex">
                    <>
                      <Button href="/coupons">Punkte einlösen</Button>

                      <Button href="/activities" btnClass="outline">
                        Zur nächste Aktivität
                      </Button>
                    </>
                  </div>
                )}
              </StickyContent>
            )}
          </div>
        </div>
      </div>
      {!XGA && (
        <StickyContent>
          {data?.inStock ? (
            <div className="buttons-flex">
              <Button
                disabled={!allZeros || loading || !data?.available}
                action={() => {
                  redeemAward();
                }}
              >
                Meinen Moment nutzen!
              </Button>
            </div>
          ) : (
            <div className="buttons-flex">
              <>
                <Button href="/coupons">Punkte einlösen</Button>

                <Button href="/activities" btnClass="outline">
                  Zur nächste Aktivität
                </Button>
              </>
            </div>
          )}
        </StickyContent>
      )}
    </section>
  );
}
